import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { MaskedReveal, FadeUp, SectionLabel, EASE_OUT } from "@/components/Motion";
import { ArrowUpRight } from "lucide-react";

const TALLY_URL = `https://tally.so/r/${process.env.REACT_APP_TALLY_ID}`;

// Warmer, human editorial photography.
const IMG_HERO_LETTER = "/images/hero-envelope.png";
const IMG_HANDS_MOTHER =
  "https://images.unsplash.com/photo-1519689680058-324335c77eba?auto=format&fit=crop&w=1400&q=80";
const IMG_KITCHEN = "/images/postcard-kalamkari.png";
const IMG_NURSERY =
  "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=1400&q=80";
const IMG_EXPERT = "/images/expert-shivani-agarwal.webp";

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section ref={ref} className="relative min-h-[100svh] pt-10 md:pt-16 overflow-hidden" data-testid="hero-section">
      <div className="editorial-container relative">
        <div className="mt-10 md:mt-14 grid grid-cols-12 gap-x-6">
          <div className="col-span-12 lg:col-span-8">
            <h1 className="font-serif tracking-tight text-ink text-[13vw] sm:text-[10vw] md:text-[8.5vw] lg:text-[7.5rem] xl:text-[8.5rem] leading-[1]">
              <MaskedReveal
                text="Letters from"
                as="span"
                className="block"
                delay={0.15}
                stagger={0.05}
                duration={1.1}
                animate
                testId="hero-line-1"
              />
              <span className="block">
                <span className="line-mask inline-block align-bottom">
                  <motion.span
                    initial={{ y: "110%" }}
                    animate={{ y: "0%" }}
                    transition={{ duration: 1.2, ease: EASE_OUT, delay: 0.45 }}
                    className="inline-block italic text-terracotta"
                  >
                    motherhood,
                  </motion.span>
                </span>
              </span>
              <MaskedReveal
                text="posted monthly."
                as="span"
                className="block"
                delay={0.75}
                stagger={0.05}
                duration={1.1}
                animate
                testId="hero-line-3"
              />
            </h1>
          </div>

          <div className="col-span-12 lg:col-span-4 flex flex-col justify-end mt-10 lg:mt-0">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: EASE_OUT, delay: 1.1 }}
              className="font-serif text-xl md:text-2xl leading-[1.4] text-ink/80 max-w-md"
            >
              A modern editorial publication for pregnancy &amp; motherhood — arriving once a month, quietly.
            </motion.p>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, ease: EASE_OUT, delay: 1.4 }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <Link to="/mail-club" className="btn-pill" data-testid="hero-subscribe-cta">
                Join the Mail Club <ArrowUpRight size={14} />
              </Link>
              <Link to="/colour-me-mama" className="btn-pill ghost" data-testid="hero-cmm-cta">
                Colour Me Mama <ArrowUpRight size={14} />
              </Link>
              <a
                href={TALLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-pill ghost"
                data-testid="hero-share-story"
              >
                Share Your Story <ArrowUpRight size={14} />
              </a>
            </motion.div>
          </div>
        </div>

        <motion.div
          style={{ y: y1 }}
          className="relative mt-16 md:mt-24 aspect-[16/9] md:aspect-[21/9] overflow-hidden bg-creamDeep"
        >
          <img
            src={IMG_HERO_LETTER}
            alt="Hands writing a letter in soft daylight"
            className="absolute inset-0 w-full h-full object-cover img-warm"
          />
        </motion.div>
      </div>
    </section>
  );
}

// One anonymous excerpt, sitting alone up top.
function FirstEnvelopeTeaser() {
  return (
    <section className="mt-24 md:mt-32" data-testid="first-envelope-teaser">
      <div className="editorial-container">
        <FadeUp>
          <div className="relative border border-ink/15 rounded-md px-6 py-12 md:px-14 md:py-16 lg:px-20 overflow-hidden">
            {/* deckled corner accent, terracotta */}
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-terracotta/10 pointer-events-none" />
            <div className="absolute -bottom-14 -left-14 w-48 h-48 rounded-full bg-sage/10 pointer-events-none" />
            <div className="relative grid grid-cols-12 gap-x-6 gap-y-8 items-end">
              <div className="col-span-12 md:col-span-8">
                <SectionLabel number="Intro" label="The First Envelope" />
                <h2 className="mt-6 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-ink">
                  Your first Safe Mama envelope is <em className="italic text-terracotta">on us.</em> 💌
                </h2>
              </div>
              <div className="col-span-12 md:col-span-4">
                <p className="font-serif text-lg md:text-xl text-ink/75 leading-relaxed">
                  We're sending our very first introductory envelope out into the world — and we'd love for
                  one to find its way to you.
                </p>
                <p className="mt-4 font-serif italic text-lg text-ink/60">
                  No purchase. No subscription. Just our first envelope, from us to you.
                </p>
                <Link to="/first-envelope" className="btn-pill mt-8" data-testid="home-first-envelope-cta">
                  Send me my envelope <ArrowUpRight size={14} />
                </Link>
              </div>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}

function TwinExcerpts() {
  return (
    <section className="mt-40 md:mt-56" data-testid="anonymous-excerpts">
      <div className="editorial-container">
        <div className="grid grid-cols-12 gap-x-6">
          <div className="col-span-12 md:col-span-10 md:col-start-2 lg:col-span-8 lg:col-start-3">
            <FadeUp>
              <p className="font-sans italic text-3xl md:text-4xl lg:text-5xl leading-[1.2] text-ink">
                &ldquo;It's insane how nobody understands all this except the mothers. Not the
                husbands, not the helpers, not even the grandmoms who are moms too but the novelty
                was many moons ago and they have probably forgotten the weight of being in the
                deep.&rdquo;
              </p>
              <div className="mt-8 overline text-ink/50">— Anonymous mama</div>
            </FadeUp>
          </div>
        </div>
      </div>
    </section>
  );
}

function Marquee() {
  const items = [
    "A Letter",
    "Postcards from the Indian Kitchen",
    "Nourishment Weekly Meal Plan",
    "An Expert Note",
    "A Thoughtful Surprise",
  ];
  const line = items.join("  ·  ") + "  ·  ";
  return (
    <section
      className="mt-40 md:mt-56 py-8 md:py-10 border-y border-ink/10 overflow-hidden"
      aria-label="What's inside each edition"
    >
      <div className="marquee-track">
        <div className="font-sans uppercase tracking-[0.18em] text-3xl md:text-4xl lg:text-5xl leading-none text-ink whitespace-nowrap pr-16">
          {line}
        </div>
        <div className="font-sans uppercase tracking-[0.18em] text-3xl md:text-4xl lg:text-5xl leading-none text-ink whitespace-nowrap pr-16" aria-hidden>
          {line}
        </div>
      </div>
    </section>
  );
}

// Ch. 01 — Inside the envelope (the 5 enclosures).
function InsidePreview() {
  const items = [
    { n: "01", title: "A Letter", body: "An anonymous, real letter from a mother — an honest reflection from someone who has walked this path. Never published online. Exclusive to the print edition." },
    { n: "02", title: "Postcards from the Indian Kitchen", body: "One recipe designed like a postcard, appropriate to the reader's stage of motherhood — illustrated by hand." },
    { n: "03", title: "Nourishment Weekly Meal Plan", body: "A weekly meal plan designed to provide practical nourishment and make everyday eating easier during pregnancy or motherhood." },
    { n: "04", title: "An Expert Note", body: "A short piece of useful guidance from a relevant professional — a counsellor, nutritionist, fitness expert, paediatrician, gynaecologist, or another appropriate maternal-health professional." },
    { n: "05", title: "A Thoughtful Surprise", body: "A small surprise included with each edition — sometimes a sample from a brand we trust, sometimes stickers, stationery or another small delight. A supporting part of the publication, chosen with care." },
  ];
  return (
    <section className="mt-40 md:mt-56 py-24 md:py-32 bg-creamDeep border-y border-ink/10" data-testid="inside-envelope">
      <div className="editorial-container">
        <FadeUp>
          <SectionLabel number="Ch. 01" label="Inside the envelope" />
          <h2 className="mt-6 font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-ink max-w-4xl">
            Everything designed to be <em className="italic text-terracotta">kept</em>, not scrolled.
          </h2>
        </FadeUp>

        <div className="mt-20 md:mt-24 grid grid-cols-12 gap-x-6 gap-y-14 md:gap-y-16">
          {items.map((m, i) => (
            <FadeUp key={m.n} className="col-span-12 md:col-span-6" delay={(i % 2) * 0.06}>
              <div className="flex items-baseline gap-6 md:gap-8 pt-6 border-t border-ink/15">
                <div className="font-serif text-5xl md:text-6xl leading-none text-ink/40 tracking-tight">{m.n}</div>
                <div className="flex-1">
                  <h3 className="font-serif text-2xl md:text-3xl leading-[1.15] text-ink">{m.title}</h3>
                  <p className="mt-4 font-serif text-lg text-ink/75 leading-relaxed max-w-md">{m.body}</p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

// Ch. 02 — Envelope Specials (previews of this month's edition).
function Manifesto() {
  return (
    <section className="mt-40 md:mt-56" data-testid="manifesto-section">
      <div className="editorial-container">
        <SectionLabel number="Ch. 02" label="Envelope Specials" />
        <h2 className="mt-6 font-serif text-5xl md:text-6xl leading-[1.05] tracking-tight text-ink max-w-3xl">
          A few pages, <em className="italic text-sage">quietly turned</em>.
        </h2>

        {/* Preview 1 — Twin anonymous excerpts (text-only, kept spare) */}
        <FadeUp className="mt-24 md:mt-28">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-20">
            {[
              { text: "I hid in the bathroom just to eat half a banana in peace.", note: "Anonymous mama" },
              { text: "No one warned me how lonely the afternoons could feel.", note: "Anonymous mama" },
            ].map((q, i) => (
              <div key={i}>
                <p className="font-sans italic text-3xl md:text-4xl lg:text-5xl leading-[1.15] text-ink">
                  &ldquo;{q.text}&rdquo;
                </p>
                <div className="mt-8 overline text-ink/50">— {q.note}</div>
              </div>
            ))}
          </div>
        </FadeUp>

        {/* Preview 2 — Postcard from the Indian Kitchen (image-led, minimal text) */}
        <FadeUp className="mt-32 md:mt-40">
          <div className="grid grid-cols-12 gap-x-6 items-center">
            <div className="col-span-12 md:col-span-7">
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={IMG_KITCHEN}
                  alt="Kalamkari postcard from Andhra Pradesh and Telangana"
                  className="absolute inset-0 w-full h-full object-cover img-warm"
                />
              </div>
            </div>
            <div className="col-span-12 md:col-span-4 md:col-start-9 mt-8 md:mt-0">
              <div className="overline text-ink/60">Postcards from the Indian Kitchen</div>
              <p className="mt-4 font-serif italic text-2xl md:text-3xl leading-[1.2] text-ink">
                Age-old Indian recipes — tailored to your stage of motherhood.
              </p>
            </div>
          </div>
        </FadeUp>

        {/* Preview 3 — This Month's Expert */}
        <FadeUp className="mt-32 md:mt-40">
          <div className="grid grid-cols-12 gap-x-6 items-center">
            <div className="col-span-12 md:col-span-5">
              <div className="relative aspect-[4/5] overflow-hidden">
                <img
                  src={IMG_EXPERT}
                  alt="Portrait of Shivani Agarwal, Clinical Counsellor and Art Therapist"
                  className="absolute inset-0 w-full h-full object-cover img-warm"
                />
              </div>
            </div>
            <div className="col-span-12 md:col-span-6 md:col-start-7 mt-8 md:mt-0">
              <div className="overline text-ink/60">This Month's Expert</div>
              <h3 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-ink">
                Shivani Agarwal
              </h3>
              <p className="mt-6 font-serif text-lg md:text-xl leading-relaxed text-ink/75 max-w-xl">
                Shivani is a Clinical Counsellor and Art Therapist, experienced in working with
                teens, youth, and adults braving a range of mental health challenges. She was born
                in North India, raised in different parts of the country, and then went on to live
                in England and Canada. Her editorial note in this issue is on the changing
                relationships with yourself and your loved ones during pregnancy and postpartum.
                You can reach her through her website <a
                  href="https://www.centreforselfdiscovery.org"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline decoration-ink/40 hover:decoration-ink text-ink"
                  data-testid="expert-website-link"
                >
                  www.centreforselfdiscovery.org
                </a>
                .
              </p>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}

function Editions() {
  const items = [
    {
      to: "/mail-club?edition=pregnancy",
      overline: "Edition A",
      title: "The Pregnancy Edition",
      body: "For the expecting. Notes on the trimesters, movement, rest, and the very quiet becoming.",
      img: "/images/edition-pregnancy.png",
      accent: "terracotta",
    },
    {
      to: "/mail-club?edition=motherhood",
      overline: "Edition B",
      title: "The Motherhood Edition",
      body: "For the after. Postpartum recovery, nourishment, sleep, and the making of a mother.",
      img: "/images/edition-motherhood.png",
      accent: "sage",
    },
  ];
  return (
    <section className="mt-40 md:mt-56" data-testid="editions-section">
      <div className="editorial-container">
        <FadeUp>
          <SectionLabel number="Ch. 03" label="The two editions" />
          <h2 className="mt-6 font-serif text-5xl md:text-6xl leading-[1.05] tracking-tight text-ink max-w-3xl">
            Choose the edition <em className="italic">for your season</em>.
          </h2>
        </FadeUp>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
          {items.map((e, i) => (
            <FadeUp key={e.to} delay={i * 0.08}>
              <Link to={e.to} className="group block relative" data-testid={`edition-card-${i}`}>
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img
                    src={e.img}
                    alt={e.title}
                    className="absolute inset-0 w-full h-full object-cover img-warm"
                  />
                </div>
                <div className="mt-6 flex items-start justify-between gap-6">
                  <div>
                    <div className="overline text-ink/50">{e.overline}</div>
                    <h3
                      className={`mt-2 font-serif text-3xl md:text-4xl italic leading-tight ${
                        e.accent === "terracotta" ? "text-terracotta" : "text-sage"
                      }`}
                    >
                      {e.title}
                    </h3>
                    <p className="mt-3 font-serif text-lg text-ink/70 max-w-md">{e.body}</p>
                  </div>
                  <ArrowUpRight className="text-ink mt-1 transition-transform group-hover:-translate-y-1 group-hover:translate-x-1" size={22} />
                </div>
              </Link>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function ColourMeMamaTeaser() {
  return (
    <section className="mt-40 md:mt-56" data-testid="cmm-teaser">
      <div className="editorial-container">
        <div className="grid grid-cols-12 gap-x-6 items-end">
          <div className="col-span-12 md:col-span-8">
            <SectionLabel number="No. 008" label="Colour Me Mama" />
            <h2 className="mt-6 font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-ink">
              Every mama has a colour. <em className="italic text-terracotta">Find out yours.</em>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 mt-8 md:mt-0">
            <p className="font-serif text-xl text-ink/75 leading-relaxed max-w-md">
              A little pause. 16 questions. No right answers, no wrong answers — just the colour that best describes the mother you already are.
            </p>
            <Link to="/colour-me-mama" className="btn-pill mt-6" data-testid="home-cmm-cta">
              Find my colour <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>

        <FadeUp delay={0.1}>
          <div className="mt-14 flex items-center gap-3 flex-wrap">
            {[
              { c: "#B24236", n: "Red" },
              { c: "#C4694A", n: "Orange" },
              { c: "#D5A24A", n: "Yellow" },
              { c: "#5C6B4F", n: "Green" },
              { c: "#3E5C6B", n: "Blue" },
              { c: "#6B4A6B", n: "Purple" },
              { c: "#E8C4B8", n: "Pink" },
            ].map((s) => (
              <div key={s.n} className="flex items-center gap-2">
                <span className="inline-block w-5 h-5 rounded-full" style={{ background: s.c }} />
                <span className="overline text-ink/60">{s.n}</span>
              </div>
            ))}
          </div>
        </FadeUp>
      </div>
    </section>
  );
}

function ReadingTeaser() {
  const cards = [
    { cat: "Postpartum", title: "The fourth trimester nobody warned you about.", src: "The Cut" },
    { cat: "Nutrition", title: "Iron, folate & the Indian plate.", src: "Healthline" },
    { cat: "Mental Wellbeing", title: "Matrescence — the making of a mother.", src: "NYT" },
  ];
  return (
    <section className="mt-40 md:mt-56" data-testid="reading-teaser">
      <div className="editorial-container">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <SectionLabel number="Ch. 04" label="The reading room" />
            <h2 className="mt-6 font-serif text-5xl md:text-6xl leading-[1.05] tracking-tight text-ink max-w-2xl">
              A curated library of reading, <em className="italic text-sage">soft on the eye</em>.
            </h2>
          </div>
          <Link to="/reading-room" className="btn-pill ghost" data-testid="reading-teaser-cta">
            All Reading <ArrowUpRight size={14} />
          </Link>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
          {cards.map((c, i) => (
            <FadeUp key={i} delay={i * 0.08}>
              <div className="border-t border-ink/20 pt-6">
                <div className="overline text-ink/50">{c.cat}</div>
                <h3 className="mt-4 font-serif italic text-2xl md:text-3xl leading-[1.15] text-ink">{c.title}</h3>
                <div className="mt-6 overline text-ink/50">Source · {c.src}</div>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function ClosingCTA() {
  return (
    <section className="mt-40 md:mt-56 py-24 md:py-32 bg-terracotta text-cream">
      <div className="editorial-container">
        <div className="grid grid-cols-12 gap-x-6 items-end">
          <div className="col-span-12 md:col-span-8">
            <div className="overline text-cream/70">Mail Club</div>
            <h2 className="mt-6 font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight">
              Subscribe. And wait for the <em className="italic">post</em>.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 mt-8 md:mt-0">
            <p className="font-serif text-xl text-cream/85 leading-relaxed">
              One envelope, delivered each month. Cancel anytime — but you probably won't.
            </p>
            <Link to="/mail-club" className="btn-pill blush mt-6" data-testid="closing-cta">
              Join the Mail Club <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  return (
    <div data-testid="home-page">
      <Hero />
      <FirstEnvelopeTeaser />
      <TwinExcerpts />
      <Marquee />
      <InsidePreview />
      <Manifesto />
      <Editions />
      <ColourMeMamaTeaser />
      <ReadingTeaser />
      <ClosingCTA />
    </div>
  );
}
